import React, { PureComponent } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { PPRouterProps } from '../../../../routes/paths';
import PPPatientOverview from './PPPatientOverview';
import PPPadElements from './PPPadElements';
import PPVaccination from './PPVaccination';
import { captureError } from '../../../../pages/captureError';
import { track } from '../../../../tracking';
import { VaccineInfo } from '../../../../api/fetchVaccineSchedule';
import { StaticContext } from 'react-router';
import PPMilestones from './PPMilestones';
import { UIMilestone } from '../../../../api/getMilestone';
import { flushUIMilestonesCache, milestonesUICache } from '../../../../modules/milestone';
import PPTreatmentPlan from './TreatmentPlan/PPTreatmentPlan';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../store/reducers';
import { getPatient } from '../../../../store/selectors/prescriptionPad';
import { Patient } from '../../../../store/types/prescription';
import PPCareCanvas from './CareCanvas/PPCareCanvas';
import PPRelatedProfiles from './PPRelatedProfiles';
import PPMedicalRecords from './MedicalRecords/PPMedicalRecords';

interface Props extends RouteComponentProps<PPRouterProps, StaticContext, undefined> {
    patient?: Patient;
}

interface State {
    vaccinationData: Partial<VaccineInfo>;
    isVaccinationDataLoading: boolean;
    isMilestoneDataLoading: boolean;
    milestoneData: UIMilestone;
}

class MainPanelComponents extends PureComponent<Props, State> {
    state: State = {
        vaccinationData: {} as Partial<VaccineInfo>,
        milestoneData: {},
        isVaccinationDataLoading: true,
        isMilestoneDataLoading: true,
    };

    fetchVaccineData = async () => {
        try {
            const result = await fetch('/vaccination-data.json?v=11');

            const res = await result.json();

            this.setState({
                isVaccinationDataLoading: false,
                vaccinationData: res as VaccineInfo,
            });
        } catch (error) {
            this.setState({
                isVaccinationDataLoading: false,
            });
            track('FETCH_VACCINE_INFO_FAILED');
            captureError(error as Error);
        }
    };

    updateMilestones = async (onSuccess?: () => void) => {
        flushUIMilestonesCache();
        const uiMilestones = await milestonesUICache();
        this.setState({ milestoneData: uiMilestones, isMilestoneDataLoading: false }, () => {
            onSuccess && onSuccess();
        });
        return uiMilestones;
    };

    fetchMilestoneData = async () => {
        try {
            return await this.updateMilestones();
        } catch (e) {
            this.setState({
                isMilestoneDataLoading: false,
            });
            track('FETCH_MILESTONE_INFO_FAILED');
            captureError(e as Error);
        }
    };

    componentDidMount(): void {
        Promise.allSettled([this.fetchVaccineData(), this.fetchMilestoneData()]).catch((e) =>
            console.error(e),
        );
    }

    render(): React.ReactNode {
        const { match, patient } = this.props;
        const {
            vaccinationData,
            isVaccinationDataLoading,
            isMilestoneDataLoading,
            milestoneData,
        } = this.state;
        const patientId = patient?.localId || patient?.id || '';
        const relations = Array.isArray(patient?.relations) ? patient?.relations || [] : [];

        switch (match.params.subRoute) {
            case 'patient-overview':
                return <PPPatientOverview patientId={patientId} />;
            case 'related-profiles':
                return <PPRelatedProfiles relations={relations} />;
            case 'vaccination':
                return (
                    <PPVaccination
                        vaccinationData={vaccinationData}
                        isVaccinationDataLoading={isVaccinationDataLoading}
                    />
                );
            case 'milestones':
                return (
                    <PPMilestones
                        milestoneData={milestoneData}
                        isMilestoneDataLoading={isMilestoneDataLoading}
                        updateMilestones={this.updateMilestones}
                    />
                );
            case 'treatment-plan':
                return <PPTreatmentPlan patientId={patientId} />;
            case 'care-canvas':
                return <PPCareCanvas />;
            case 'medical-records':
                return <PPMedicalRecords />;
            default:
                return <PPPadElements />;
        }
    }
}

export default connect((state: GlobalState) => ({
    patient: getPatient(state),
}))(withRouter(MainPanelComponents));
